/**
 * BracketExportImage - Renders a full bracket as a capturable image.
 *
 * Traditional bracket layout:
 *   Left:  East (top) + West (bottom) → Final Four
 *   Right: South (top) + Midwest (bottom) → Final Four
 *   Center: Championship + Champion
 *
 * Uses inline styles throughout for reliable html-to-image capture.
 * Rendered off-screen, captured via toPng, then hidden.
 */

import { forwardRef, useMemo } from 'react';

const REGIONS_ORDER = ['East', 'West', 'South', 'Midwest'];

const REGION_COLORS = {
  East: '#3b82f6',
  West: '#ef4444',
  South: '#10b981',
  Midwest: '#f59e0b',
};

const SEED_COLORS = {
  top: '#10b981',    // 1-4
  mid: '#3b82f6',    // 5-8
  low: '#f59e0b',    // 9-12
  bottom: '#ef4444', // 13-16
};

function getSeedColor(seed) {
  if (seed <= 4) return SEED_COLORS.top;
  if (seed <= 8) return SEED_COLORS.mid;
  if (seed <= 12) return SEED_COLORS.low;
  return SEED_COLORS.bottom;
}

/**
 * Build a map: { region: { round: [ { slot, team1, team2, winner } ] } }
 */
function organizePicks(picks, entriesMap) {
  const regionPicks = {};
  REGIONS_ORDER.forEach(r => { regionPicks[r] = {}; });
  const finalFour = { 5: [], 6: [] };

  for (const pick of picks) {
    if (pick.round_number >= 5) {
      if (!finalFour[pick.round_number]) finalFour[pick.round_number] = [];
      finalFour[pick.round_number].push(pick);
      continue;
    }

    // Determine region from entry or slot math
    let region = null;
    const entry = pick.team1_entry_id ? entriesMap[pick.team1_entry_id]
      : pick.team2_entry_id ? entriesMap[pick.team2_entry_id]
      : pick.winner_entry_id ? entriesMap[pick.winner_entry_id]
      : null;
    if (entry?.region) {
      region = entry.region;
    } else {
      const gamesPerRegion = Math.pow(2, 4 - pick.round_number);
      const regionIdx = Math.floor((pick.game_slot - 1) / gamesPerRegion);
      region = REGIONS_ORDER[regionIdx];
    }

    if (!regionPicks[region]) regionPicks[region] = {};
    if (!regionPicks[region][pick.round_number]) regionPicks[region][pick.round_number] = [];
    regionPicks[region][pick.round_number].push(pick);
  }

  // Sort by game_slot within each round
  for (const region of REGIONS_ORDER) {
    for (const round of Object.keys(regionPicks[region])) {
      regionPicks[region][round].sort((a, b) => a.game_slot - b.game_slot);
    }
  }
  for (const round of Object.keys(finalFour)) {
    finalFour[round].sort((a, b) => a.game_slot - b.game_slot);
  }

  return { regionPicks, finalFour };
}

function TeamCell({ entry, isWinner, reverse }) {
  if (!entry) {
    return (
      <div style={{
        height: 18,
        display: 'flex',
        alignItems: 'center',
        padding: '0 4px',
        fontSize: 10,
        color: '#64748b',
        fontStyle: 'italic',
      }}>
        TBD
      </div>
    );
  }

  const seedColor = getSeedColor(entry.seed);

  return (
    <div style={{
      height: 18,
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      padding: '0 4px',
      flexDirection: reverse ? 'row-reverse' : 'row',
      background: isWinner ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
      borderRadius: 2,
    }}>
      <span style={{
        fontSize: 9,
        fontWeight: 700,
        color: seedColor,
        width: 14,
        textAlign: 'center',
        flexShrink: 0,
      }}>
        {entry.seed}
      </span>
      <span style={{
        fontSize: 10,
        fontWeight: isWinner ? 700 : 400,
        color: isWinner ? '#e2e8f0' : '#94a3b8',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textAlign: reverse ? 'right' : 'left',
      }}>
        {entry.team_abbreviation || entry.team_name?.slice(0, 6) || '???'}
      </span>
    </div>
  );
}

function Matchup({ pick, entriesMap, reverse, roundNumber }) {
  const team1 = pick.team1_entry_id ? entriesMap[pick.team1_entry_id] : null;
  const team2 = pick.team2_entry_id ? entriesMap[pick.team2_entry_id] : null;
  const winnerId = pick.winner_entry_id;

  return (
    <div style={{
      width: 88,
      border: '1px solid rgba(148, 163, 184, 0.15)',
      borderRadius: 3,
      overflow: 'hidden',
      background: 'rgba(15, 23, 42, 0.6)',
      flexShrink: 0,
    }}>
      <TeamCell entry={team1} isWinner={winnerId && winnerId === team1?.id} reverse={reverse} />
      <div style={{ height: 1, background: 'rgba(148, 163, 184, 0.12)' }} />
      <TeamCell entry={team2} isWinner={winnerId && winnerId === team2?.id} reverse={reverse} />
    </div>
  );
}

/**
 * RegionColumn renders rounds 1-4 for a single region.
 * `reverse` flips the column order for right-side regions.
 */
function RegionColumn({ regionName, picks, entriesMap, reverse }) {
  const color = REGION_COLORS[regionName];
  const rounds = [1, 2, 3, 4];
  const displayRounds = reverse ? [...rounds].reverse() : rounds;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Region label */}
      <div style={{
        fontSize: 11,
        fontWeight: 800,
        color,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        textAlign: reverse ? 'right' : 'left',
        padding: '0 4px',
        marginBottom: 2,
      }}>
        {regionName}
      </div>

      {/* Rounds */}
      <div style={{
        display: 'flex',
        flexDirection: reverse ? 'row-reverse' : 'row',
        alignItems: 'center',
        gap: 4,
      }}>
        {displayRounds.map(round => {
          const roundPicks = picks[round] || [];
          const gamesInRound = Math.pow(2, 4 - round); // R1=8, R2=4, R3=2, R4=1

          // Calculate vertical spacing: each matchup is 37px (18+1+18)
          // R1 matchups are stacked tight, later rounds center between feeders
          const matchupHeight = 37;
          const r1Gap = 3;

          // For later rounds, compute gap to center between feeder matchups
          let gap = r1Gap;
          if (round === 2) gap = matchupHeight + r1Gap * 2 + r1Gap;
          if (round === 3) gap = matchupHeight * 3 + r1Gap * 5 + r1Gap;
          if (round === 4) gap = matchupHeight * 7 + r1Gap * 11 + r1Gap;

          // Compute top padding to center later rounds
          let paddingTop = 0;
          if (round === 2) paddingTop = (matchupHeight + r1Gap) / 2;
          if (round === 3) paddingTop = (matchupHeight * 2 + r1Gap * 2 + (matchupHeight + r1Gap)) / 2;
          if (round === 4) paddingTop = (matchupHeight * 4 + r1Gap * 4 + (matchupHeight * 3 + r1Gap * 4)) / 2;

          return (
            <div
              key={round}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: gap,
                paddingTop: paddingTop,
              }}
            >
              {roundPicks.slice(0, gamesInRound).map((pick, i) => (
                <Matchup
                  key={`${round}-${i}`}
                  pick={pick}
                  entriesMap={entriesMap}
                  reverse={reverse}
                  roundNumber={round}
                />
              ))}
              {/* Fill empty slots if picks are missing */}
              {Array.from({ length: Math.max(0, gamesInRound - roundPicks.length) }).map((_, i) => (
                <Matchup
                  key={`empty-${round}-${i}`}
                  pick={{ team1_entry_id: null, team2_entry_id: null, winner_entry_id: null }}
                  entriesMap={entriesMap}
                  reverse={reverse}
                  roundNumber={round}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FinalFourCenter({ finalFour, entriesMap, bracketName, strategy }) {
  const f4Picks = finalFour[5] || [];
  const champPick = (finalFour[6] || [])[0];

  const champion = champPick?.winner_entry_id
    ? entriesMap[champPick.winner_entry_id]
    : null;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      padding: '0 12px',
      minWidth: 160,
    }}>
      {/* Champion */}
      <div style={{
        textAlign: 'center',
        padding: '8px 16px',
        background: champion
          ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.1))'
          : 'rgba(30, 41, 59, 0.5)',
        borderRadius: 8,
        border: champion
          ? '2px solid rgba(245, 158, 11, 0.4)'
          : '2px dashed rgba(148, 163, 184, 0.2)',
        minWidth: 140,
      }}>
        <div style={{ fontSize: 8, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
          Champion
        </div>
        {champion ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#f1f5f9' }}>
              {champion.team_abbreviation || champion.team_name}
            </div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
              #{champion.seed} {champion.region}
            </div>
          </>
        ) : (
          <div style={{ fontSize: 11, color: '#475569', fontStyle: 'italic' }}>TBD</div>
        )}
      </div>

      {/* Championship game */}
      {champPick && (
        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: 8, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', textAlign: 'center', marginBottom: 3, letterSpacing: '0.05em' }}>
            Championship
          </div>
          <Matchup pick={champPick} entriesMap={entriesMap} reverse={false} roundNumber={6} />
        </div>
      )}

      {/* Final Four games */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {f4Picks.map((pick, i) => (
          <div key={i}>
            <div style={{ fontSize: 8, fontWeight: 600, color: '#64748b', textAlign: 'center', marginBottom: 2 }}>
              {i === 0 ? 'E vs W' : 'S vs MW'}
            </div>
            <Matchup pick={pick} entriesMap={entriesMap} reverse={false} roundNumber={5} />
          </div>
        ))}
      </div>
    </div>
  );
}

const BracketExportImage = forwardRef(function BracketExportImage(
  { bracket, entries },
  ref
) {
  const entriesMap = useMemo(() => {
    const map = {};
    (entries || []).forEach(e => { map[e.id] = e; });
    return map;
  }, [entries]);

  const picks = useMemo(() => bracket?.picks || [], [bracket?.picks]);
  const { regionPicks, finalFour } = useMemo(
    () => organizePicks(picks, entriesMap),
    [picks, entriesMap]
  );

  const totalPicks = picks.filter(p => p.winner_entry_id).length;
  const upsets = picks.filter(p => p.is_upset === 1).length;

  return (
    <div
      ref={ref}
      style={{
        width: 1200,
        minHeight: 700,
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        padding: '20px 16px 16px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#e2e8f0',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle grid pattern */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(148,163,184,0.04) 1px, transparent 0)',
        backgroundSize: '24px 24px',
        pointerEvents: 'none',
      }} />

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        position: 'relative',
        zIndex: 1,
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#f1f5f9' }}>
            {bracket?.bracket_name || 'My Bracket'}
          </div>
          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
            {totalPicks}/63 picks · {upsets} upsets · {bracket?.strategy || 'manual'} strategy
          </div>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <div style={{
            fontSize: 10,
            fontWeight: 700,
            color: '#64748b',
            letterSpacing: '0.05em',
          }}>
            Built with
          </div>
          <div style={{
            fontSize: 13,
            fontWeight: 900,
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.02em',
          }}>
            UKnew AI
          </div>
        </div>
      </div>

      {/* Bracket body */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Left side: East (top) + West (bottom) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <RegionColumn
            regionName="East"
            picks={regionPicks.East || {}}
            entriesMap={entriesMap}
            reverse={false}
          />
          <RegionColumn
            regionName="West"
            picks={regionPicks.West || {}}
            entriesMap={entriesMap}
            reverse={false}
          />
        </div>

        {/* Center: Final Four + Championship */}
        <FinalFourCenter
          finalFour={finalFour}
          entriesMap={entriesMap}
          bracketName={bracket?.bracket_name}
          strategy={bracket?.strategy}
        />

        {/* Right side: South (top) + Midwest (bottom) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <RegionColumn
            regionName="South"
            picks={regionPicks.South || {}}
            entriesMap={entriesMap}
            reverse={true}
          />
          <RegionColumn
            regionName="Midwest"
            picks={regionPicks.Midwest || {}}
            entriesMap={entriesMap}
            reverse={true}
          />
        </div>
      </div>

      {/* Footer */}
      <div style={{
        marginTop: 12,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{ fontSize: 9, color: '#475569' }}>
          NCAA March Madness {bracket?.season || 2026} · Generated {new Date().toLocaleDateString()}
        </div>
        <div style={{ fontSize: 9, color: '#475569' }}>
          uknew.app/bracket
        </div>
      </div>
    </div>
  );
});

export default BracketExportImage;
